import React, { useContext } from "react";
import { AppContext } from "./context";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Tooltip from "react-tooltip";
import { HiOutlinePlayCircle, HiOutlineArrowPath, HiOutlineCheckCircle, HiOutlineClock } from "react-icons/hi2";

import Rating from "../rating";

import api from "../../../../../services/api";

export default function Home() {
  const context = useContext(AppContext);

  if (!context.workshop) return <div />;
  if (context.index !== 0) return <div />;

  const isResuming = !!context.userWorkshop;
  const lastStep = context.userWorkshop?.step_id || 0;

  function renderAction() {
    if (context.userWorkshop) {
      return (
        <button
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          onClick={() => context.setIndex(Math.max(1, Math.min(lastStep, context.workshop.steps.length)))}>
          <HiOutlineArrowPath className="w-5 h-5" />
          Continue from Step {Math.min(lastStep, context.workshop.steps.length)}
        </button>
      );
    }
    return (
      <button
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
        onClick={async () => {
          if (!context.user) return context.setIndex(1);
          toast.success("Workshop started");
          const { data } = await api.post(`/workshop_user`, {
            workshop_id: context.workshop._id,
            workshop_name: context.workshop.name,
            workshop_version: context.workshop.version,
          });
          context.setUserWorkshop(data);
          context.setIndex(1);
        }}>
        <HiOutlinePlayCircle className="w-5 h-5" />
        Start Workshop
      </button>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Hero section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8">
        <div className="flex flex-col md:flex-row">
          <div className="flex-1 p-8">
            {context.workshop.category && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 mb-4">
                {context.workshop.category}
              </span>
            )}
            <h1 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">{context.workshop.name}</h1>

            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-500">
              {context.workshop.read_time && (
                <span className="flex items-center gap-1.5">
                  <HiOutlineClock className="w-4 h-4" />
                  {context.workshop.read_time}
                </span>
              )}
              {context.workshop.steps?.length > 0 && (
                <span className="flex items-center gap-1.5">
                  {context.workshop.steps.length} steps
                </span>
              )}
              {context.workshop.version && (
                <span>v{context.workshop.version}</span>
              )}
            </div>

            {context.workshop.description && (
              <p className="text-gray-600 text-base leading-relaxed mb-6">{context.workshop.description}</p>
            )}

            <div className="flex flex-wrap items-center gap-4 mb-6">
              {!!context.workshop.rating_score && (
                <div className="flex items-center gap-2">
                  <Rating value={context.workshop.rating_score} />
                  <Link to={`/rating?workshop_id=${context.workshop._id.toString()}`} className="text-sm text-blue-600 hover:underline font-medium">
                    {context.workshop.rating_score} ({context.workshop.rating_count} reviews)
                  </Link>
                </div>
              )}
              <span className="text-xs text-gray-400">
                Updated {new Date(context.workshop.updated_at).toLocaleDateString()}
              </span>
            </div>

            {renderAction()}
          </div>

          {context.workshop.image && (
            <div className="md:w-[280px] flex-shrink-0 p-6 flex items-center justify-center">
              <img
                src={context.workshop.image}
                className="w-full h-auto max-h-[220px] object-cover rounded-xl shadow-sm"
                alt={context.workshop.name}
              />
            </div>
          )}
        </div>
      </div>

      {/* Two columns: Steps + Contributors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <StepList />
        </div>
        <div>
          <Contributors />
        </div>
      </div>
    </div>
  );
}

const StepList = () => {
  const context = useContext(AppContext);
  const steps = context.workshop.steps || [];
  const lastCompletedStep = context.userWorkshop?.step_id || 0;
  const isStarted = !!context.userWorkshop;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Workshop Steps</h3>
        {isStarted && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
            {Math.min(lastCompletedStep, steps.length)}/{steps.length} completed
          </span>
        )}
      </div>

      <div className="space-y-1">
        {steps.map((step, index) => {
          const stepNum = index + 1;
          const isCompleted = isStarted && lastCompletedStep > stepNum;
          const isCurrent = isStarted && lastCompletedStep === stepNum;
          const isLocked = !isStarted;

          return (
            <button
              key={index}
              className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200 group ${
                isCurrent
                  ? "bg-blue-50 border border-blue-200 shadow-sm"
                  : isCompleted
                    ? "bg-green-50/50 hover:bg-green-50 border border-transparent"
                    : "hover:bg-gray-50 border border-transparent"
              }`}
              onClick={() => {
                if (context.userWorkshop) context.setIndex(stepNum);
                else toast.error(`Click "Start Workshop" to begin`);
              }}>
              {/* Step indicator */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-semibold transition-colors ${
                isCompleted
                  ? "bg-green-100 text-green-700"
                  : isCurrent
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-500"
              }`}>
                {isCompleted ? (
                  <HiOutlineCheckCircle className="w-5 h-5" />
                ) : (
                  stepNum
                )}
              </div>

              {/* Step title */}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium truncate ${
                  isCompleted ? "text-green-800" : isCurrent ? "text-blue-800" : "text-gray-700"
                }`}>
                  {step.title}
                </div>
              </div>

              {/* Status badge */}
              {isCurrent && (
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                  In progress
                </span>
              )}
              {isCompleted && (
                <span className="text-xs text-green-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  Revisit
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Contributors = () => {
  const context = useContext(AppContext);
  const contributors = context.workshop.contributors || [];
  if (!contributors.length) return <div />;

  const map = {};
  const uniqueContributors = [];
  contributors.forEach((contributor) => {
    if (!map[contributor.user_id]) {
      map[contributor.user_id] = true;
      uniqueContributors.push(contributor);
    }
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        Contributors
        <span className="text-sm font-normal text-gray-500 ml-2">({uniqueContributors.length})</span>
      </h3>
      <div className="space-y-3">
        {uniqueContributors.slice(0, 8).map((contributor) => (
          <div key={contributor._id} className="flex items-center gap-3">
            <img
              className="w-8 h-8 rounded-full object-cover border border-gray-200"
              src={contributor.user_avatar}
              alt={contributor.user_name}
            />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{contributor.user_name}</div>
              <div className="text-xs text-gray-400">{new Date(contributor.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        ))}
        {uniqueContributors.length > 8 && (
          <div className="text-xs text-gray-500 text-center pt-2">
            +{uniqueContributors.length - 8} more contributors
          </div>
        )}
      </div>
    </div>
  );
};
